import React, { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, PlusCircle, XCircle, FileUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { showError, showSuccess } from '@/utils/toast';

interface Translator {
  id: number;
  name: string;
}

interface Program {
  id: number;
  program_name: string;
}

interface ImageState {
  url: string;
  blobId?: any;
  isNew: boolean;
  file?: File;
}

interface EditDownloadFormProps {
  downloadId: number;
  onSave: (updatedDownload: any) => void;
  onCancel: () => void;
  uploaderId?: number;
  uploadDate?: string;
  gameTitle?: string;
}

const EditDownloadForm: React.FC<EditDownloadFormProps> = ({ downloadId, onSave, onCancel, uploaderId, uploadDate, gameTitle }) => {
  const { isAdmin, translatorId, currentUser } = useAuth();
  const [download, setDownload] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState("");
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [showAddTranslator, setShowAddTranslator] = useState(false);
  const [newProgram, setNewProgram] = useState('');
  const [newTranslator, setNewTranslator] = useState('');
  const [description, setDescription] = useState("");
  const [selectedTranslators, setSelectedTranslators] = useState<Translator[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<Program[]>([]);
  const [demo, setIsDemo] = useState(false);
  const [newGameFile, setNewGameFile] = useState<File | null>(null);
  const [images, setImages] = useState<ImageState[]>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [banwarning, setBanwarning] = useState(false);
  const [createdAt, setCreatedAt] = useState("");

  const [availableTranslators, setAvailableTranslators] = useState<{ active: Translator[]; inactive: Translator[] }>({ active: [], inactive: [] });
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Ellenőrizni, hogy az aktuális felhasználó a feltöltő-e és mennyi idő telt el
  const isCurrentUserUploader = uploaderId === currentUser?.id;
  const canUploadFile = isAdmin || (isCurrentUserUploader && isWithin24Hours());

  function isWithin24Hours(): boolean {
    if (!uploadDate) return false;

    try {
      // Az uploadDate format: "2024.09.06, 14:30" vagy ISO format
      // Szükséges konvertálni Date objektumra
      let uploadDateTime: Date;

      if (uploadDate.includes('.')) {
        // Format: "2024.09.06, 14:30"
        const parts = uploadDate.replace(/,/, '').split(/[.\s:]+/);
        if (parts.length >= 5) {
          uploadDateTime = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2]),
            parseInt(parts[3]),
            parseInt(parts[4])
          );
        } else {
          return false;
        }
      } else {
        // ISO format vagy más
        uploadDateTime = new Date(uploadDate);
      }

      const now = new Date();
      const elapsedMs = now.getTime() - uploadDateTime.getTime();
      const elapsedHours = elapsedMs / (1000 * 60 * 60);
      return elapsedHours < 24;
    } catch (e) {
      console.error("Error parsing uploadDate:", uploadDate, e);
      return false;
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [downloadRes, translatorsRes, programsRes] = await Promise.all([
          fetch(`/api/translations/${downloadId}`),
          fetch(`/api/translators/all`),
          fetch(`/api/program`)
        ]);

        if (!downloadRes.ok) throw new Error("Hiba a letöltés betöltésekor");
        if (!translatorsRes.ok) throw new Error("Hiba a fordítók lekérdezésekor");
        if (!programsRes.ok) throw new Error("Hiba a programok lekérdezésekor");

        const downloadData = await downloadRes.json();
        const translatorsData = await translatorsRes.json();
        const programsData = await programsRes.json();

        setDownload(downloadData);
        setDescription(downloadData.description || "");
        setVersion(downloadData.version); // <-- HIÁNYZÓ RÉSZ JAVÍTVA
        setImages(downloadData.pictures.map((img: any) => ({
          url: img.url,
          blobId: img.blobId,
          isNew: false,
        })));

        const allTranslators = [...translatorsData.active, ...translatorsData.inactive];
        const matchedTranslators = allTranslators.filter(t => downloadData.uploader_ids?.includes(t.id));
        setSelectedTranslators(matchedTranslators);
        setAvailableTranslators(translatorsData);

        setAvailablePrograms(programsData);

        if (downloadData.uploaderprograms && Array.isArray(downloadData.uploaderprograms)) {
          setSelectedPrograms(downloadData.uploaderprograms);
        } else {
          setSelectedPrograms([]);
        }

        setIsDemo(downloadData.demo || false);
        setIsAnonymous(downloadData.is_anonymous || false);
        setBanwarning(downloadData.banwarning || false);
        if (downloadData.created_at) {
          const date = new Date(downloadData.created_at);
          setCreatedAt(date.toISOString().slice(0, 16));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Nem sikerült betölteni az adatokat.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [downloadId]);



  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const newImageStates: ImageState[] = newFiles.map(file => ({
        url: URL.createObjectURL(file),
        file: file,
        isNew: true,
      }));
      setImages(prev => [...prev, ...newImageStates]);
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const imageToRemove = images[indexToRemove];
    if (imageToRemove.isNew) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const allowedExt = ['zip', 'exe'];
    const ext = selected.name.split('.').pop()?.toLowerCase();
    if (!ext || !allowedExt.includes(ext)) {
      showError("Csak .zip, .exe fájl tölthető fel.");
      setNewGameFile(null); // Töröljük a rossz fájlt
      e.target.value = ''; // Input mező kiürítése
      return;
    }
    setNewGameFile(selected);
  };

  // JAVÍTOTT, EGYSÉGESÍTETT TOGGLE FÜGGVÉNY
  const handleToggle = <T extends { id: number }>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    item: T
  ) => {
    setter(prev =>
      prev.some(p => p.id === item.id)
        ? prev.filter(p => p.id !== item.id)
        : [...prev, item]
    );
  };

  const handleTranslatorToggle = (translator: Translator) => {
    handleToggle(setSelectedTranslators, translator);
    if (isAnonymous) {
      setIsAnonymous(false);
    }
  };

  const handleAnonymousChange = (checked: boolean) => {
    setIsAnonymous(checked);
    if (checked && translatorId) {
      const allTranslators = [...availableTranslators.active, ...availableTranslators.inactive];
      const currentUserTranslator = allTranslators.find(t => t.id === translatorId);
      if (currentUserTranslator) {
        setSelectedTranslators([currentUserTranslator]);
      } else {
        // Ha a felhasználó valamiért nincs a listában, csak beállítjuk anonra, de nem választunk fordítót
        setSelectedTranslators([]);
      }
    }
  };

  const handleAddNewTranslator = async () => {
    const trimmedName = newTranslator.trim();
    if (!trimmedName) {
      showError("A fordító neve nem lehet üres!");
      return;
    }

    const allTranslators = [...availableTranslators.active, ...availableTranslators.inactive];
    if (allTranslators.some(t => t.name.toLowerCase() === trimmedName.toLowerCase())) {
      showError("Ez a fordító már létezik!");
      return;
    }

    try {
      const res = await fetch("/api/translator", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmedName })
      });

      if (!res.ok) throw new Error("Hiba történt a létrehozáskor");

      const newTranslatorObject: Translator = await res.json();

      setAvailableTranslators(prev => ({
        ...prev,
        active: [...prev.active, newTranslatorObject]
      }));
      setSelectedTranslators(prev => [...prev, newTranslatorObject]);
      setNewTranslator('');
      setShowAddTranslator(false);
      showSuccess(`"${trimmedName}" hozzáadva és kiválasztva.`);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Ismeretlen hiba");
    }
  };

  const handleAddNewProgram = async () => {
    const trimmedName = newProgram.trim();
    if (!trimmedName) {
      showError("A program neve nem lehet üres!");
      return;
    }

    if (availablePrograms.some(p => p.program_name.toLowerCase() === trimmedName.toLowerCase())) {
      showError("Ez a program már létezik!");
      return;
    }

    try {
      const res = await fetch("/api/program", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmedName })
      });

      if (!res.ok) throw new Error("Hiba történt a létrehozáskor");

      const newProgramObject: Program = await res.json();

      setAvailablePrograms(prev => [...prev, newProgramObject]);
      setSelectedPrograms(prev => [...prev, newProgramObject]);
      setNewProgram('');
      setShowAddProgram(false);
      showSuccess(`"${trimmedName}" hozzáadva és kiválasztva.`);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Ismeretlen hiba");
    }
  };

  // JAVÍTOTT MENTÉS FÜGGVÉNY
  const normalizeDescription = (text: string): string => {
    if (!text) return text;
    // Több egymást követő newline-t egyetre cserélni
    return text.replace(/\n\n+/g, '\n\n').trim();
  };

  const handleSaveClick = async () => {
    // Korai kilépés, ha már mentés alatt van
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Ellenőrizni, hogy a fájl feltöltés jogosult-e
      const fileWasChanged = newGameFile !== null;
      if (fileWasChanged && !canUploadFile) {
        showError("Nem jogosult fájlt feltölteni. Csak az első 24 órában lehetséges, vagy admin vagy.");
        return;
      }

      const formData = new FormData();
      formData.append('description', normalizeDescription(description));
      formData.append('version', version);
      formData.append('demo', String(demo));
      formData.append('is_anonymous', String(isAnonymous));
      formData.append('banwarning', String(banwarning));
      if (isAdmin && createdAt) {
        formData.append('created_at', createdAt);
      }
      selectedPrograms.forEach(p => formData.append('program_ids[]', String(p.id)));
      selectedTranslators.forEach(t => formData.append('translators[]', String(t.id)));
      if (download?.game_id) {
        formData.append('game_id', download.game_id);
      }

      // Küldjük a megtartandó képek URL-jeit
      const keptImages = images.filter(img => !img.isNew).map(img => img.url);
      keptImages.forEach(url => formData.append('kept_images[]', url));
      const keptBlobIds = images
        .filter(img => !img.isNew && img.blobId)
        .map(img => img.blobId);

      keptBlobIds.forEach(id => formData.append('kept_image_blob_ids[]', id));

      // Új képfájlok feltöltése
      images.filter(img => img.isNew && img.file).forEach(img => {
        formData.append('images[]', img.file);
      });

      // Új játékfájl hozzáadása, ha jogosult
      if (newGameFile && canUploadFile) {
        formData.append('game_files', newGameFile);
        formData.append('file_changed', 'true');
      }

      // Fetch
      const response = await fetch(`/api/upload/${downloadId}`, {
        method: "POST",
        body: formData,
        headers: {
          'X-HTTP-Method-Override': 'PATCH'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Mentés sikertelen");
      }

      const refreshedRes = await fetch(`/api/translations/${downloadId}`);
      if (!refreshedRes.ok) throw new Error("Frissített adat lekérése sikertelen");
      const refreshedData = await refreshedRes.json();

      // Frissítsd az form adatait az API válaszból
      setDownload(refreshedData);
      setDescription(refreshedData.description || "");
      setVersion(refreshedData.version);
      setImages(refreshedData.pictures.map((img: any) => ({
        url: img.url,
        blobId: img.blobId,
        isNew: false,
      })));

      // Frissítsd a kiválasztott fordítókat
      const allTranslators = [...availableTranslators.active, ...availableTranslators.inactive];
      const matchedTranslators = allTranslators.filter(t => refreshedData.uploader_ids?.includes(t.id));
      setSelectedTranslators(matchedTranslators);

      // Frissítsd a kiválasztott programokat
      if (refreshedData.uploaderprograms && Array.isArray(refreshedData.uploaderprograms)) {
        setSelectedPrograms(refreshedData.uploaderprograms);
      } else {
        setSelectedPrograms([]);
      }

      setIsDemo(refreshedData.demo || false);
      setIsAnonymous(refreshedData.is_anonymous || false);
      setBanwarning(refreshedData.banwarning || false);

      // Töröljük az új feltöltött játékfájlt az admin formból
      setNewGameFile(null);

      // A szülő komponens (pl. GameDownloadsTab) eltérő mezőneveket használhat
      const dataForParent = {
        ...refreshedData,
        translater_ids: refreshedData.uploader_ids || [],
        translaterPrograms: refreshedData.uploaderprograms || [],
      };

      onSave(dataForParent);

    } catch (error) {
      console.error("Mentési hiba:", error);
      alert(error instanceof Error ? error.message : "Hiba történt mentés közben.");
    } finally {
      setIsSaving(false);
    }
  };


  if (isLoading) return <p>Betöltés...</p>;
  if (error || !download) return <p className="text-red-500">{error || "Hiba történt."}</p>;

  return (
    <Card className="bg-secondary border-primary p-4 my-4 border-2 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-bold text-primary">Letöltés szerkesztése</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Verzió */}
        <div>
          <Label htmlFor={`version-${download.id}`} className="text-muted-foreground">Verzió <span className="text-red-500">*</span></Label>
          <Input
            id={`version-${download.id}`}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="mt-1 bg-card text-card-foreground border-border focus:border-primary focus:ring-primary"
            placeholder="Pl. 1.0, 1.1 patch"
            required
          />
        </div>

        {/* Leírás */}
        <div>
          <Label htmlFor="desc" className="text-muted-foreground">Leírás</Label>
          <Textarea
            id="desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 bg-card text-card-foreground border-border focus:border-primary focus:ring-primary min-h-[100px]"
          />
        </div>

        {/* Fordítók */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-muted-foreground">Fordítók</Label>
            <div className="flex items-center space-x-2">
              <Switch id="is-anonymous" checked={isAnonymous} onCheckedChange={handleAnonymousChange} />
              <Label htmlFor="is-anonymous" className="text-muted-foreground text-sm">Névtelen</Label>
              <Button type="button" variant="ghost" size="icon" onClick={() => setShowAddTranslator(prev => !prev)} className="h-6 w-6">
                <PlusCircle className="h-4 w-4 text-primary" />
              </Button>
            </div>
          </div>
          {showAddTranslator && (
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Új fordító neve..."
                value={newTranslator}
                onChange={(e) => setNewTranslator(e.target.value)}
                className="bg-card text-card-foreground border-border"
              />
              <Button type="button" onClick={handleAddNewTranslator}>Hozzáad</Button>
            </div>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between" disabled={isAnonymous}>
                {selectedTranslators.length > 0 ? selectedTranslators.map(t => t.name).join(', ') : "Válassz fordítót..."}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <div className="max-h-60 overflow-y-auto">
                {availableTranslators.active.map(t => (
                  <div key={t.id} className="flex items-center p-2 hover:bg-muted" onClick={() => handleTranslatorToggle(t)}>
                    <Checkbox checked={selectedTranslators.some(sel => sel.id === t.id)} className="mr-2" />
                    <Label>{t.name}</Label>
                  </div>
                ))}

                {availableTranslators.inactive.length > 0 && (
                  <>
                    <div className="px-2 pt-2 text-xs text-muted-foreground">Inaktív fordítók</div>
                    {availableTranslators.inactive.map(t => (
                      <div key={t.id} className="flex items-center p-2 hover:bg-muted" onClick={() => handleTranslatorToggle(t)}>
                        <Checkbox checked={selectedTranslators.some(sel => sel.id === t.id)} className="mr-2" />
                        <Label>{t.name}</Label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Programok */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-muted-foreground">Programok</Label>
            <Button type="button" variant="ghost" size="icon" onClick={() => setShowAddProgram(prev => !prev)} className="h-6 w-6">
              <PlusCircle className="h-4 w-4 text-primary" />
            </Button>
          </div>
          {showAddProgram && (
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Új program neve..."
                value={newProgram}
                onChange={(e) => setNewProgram(e.target.value)}
                className="bg-card text-card-foreground border-border"
              />
              <Button type="button" onClick={handleAddNewProgram}>Hozzáad</Button>
            </div>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                {selectedPrograms.length > 0 ? selectedPrograms.map(p => p.program_name).join(', ') : "Válassz programot..."}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <div className="max-h-60 overflow-y-auto">
                {availablePrograms.map(program => (
                  <div
                    key={program.id}
                    className="flex items-center p-2 hover:bg-muted"
                    // JAVÍTOTT ONCLICK HÍVÁS
                    onClick={() => handleToggle(setSelectedPrograms, program)}
                  >
                    <Checkbox checked={selectedPrograms.some(p => p.id === program.id)} className="mr-2" />
                    <Label>{program.program_name}</Label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Kép feltöltése */}
        <div>
          <Label className="text-muted-foreground">Képek kezelése</Label>
          <div className="mt-2 flex flex-wrap gap-2 p-2 border border-border rounded-md bg-card">
            {images.map((image, index) => (
              <div key={image.blobId ?? index} className="relative">
                <img src={image.url} alt={`Preview ${index}`} className="w-40 h-24 object-cover rounded-md" />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                  onClick={() => handleRemoveImage(index)}
                >
                  <XCircle size={14} />
                </Button>
              </div>
            ))}
            {images.length === 0 && <p className="text-sm text-muted-foreground p-4">Nincsenek képek feltöltve.</p>}
          </div>
          <Input
            id={`img-${download.id}`}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            className="mt-2 bg-card text-card-foreground border-border"
          />
        </div>

        {/* Admin beállítások */}
        {isAdmin && (
          <div className="border-t border-border pt-4">
            {/* Létrehozás dátuma */}
            <div className="mb-4">
              <Label htmlFor="created-at" className="text-muted-foreground">Létrehozás dátuma</Label>
              <Input
                id="created-at"
                type="datetime-local"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                className="mt-1 bg-card text-card-foreground border-border focus:border-primary focus:ring-primary"
              />
            </div>

            {/* Fájl cseréje */}
            <div>
              <Label htmlFor="game-file-upload" className="text-muted-foreground flex items-center gap-2">
                <FileUp size={16} /> Fájl cseréje (.zip, .exe)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">Csak akkor válassz fájlt, ha le szeretnéd cserélni a meglévőt. A fájl neve nem fog megváltozni.</p>
              <Input id="game-file-upload" type="file" onChange={handleFileChange} className="mt-1 bg-card text-card-foreground border-border" />
              {newGameFile && <p className="text-sm mt-2 text-green-400">Új fájl kiválasztva: {newGameFile.name}</p>}
            </div>
          </div>
        )}

        {/* Translator fájl feltöltés (csak az első 24 órában) */}
        {!isAdmin && isCurrentUserUploader && isWithin24Hours() && (
          <div className="border-t border-border pt-4">
            <div>
              <Label htmlFor="game-file-upload-translator" className="text-muted-foreground flex items-center gap-2">
                <FileUp size={16} /> Fájl cseréje (.zip, .exe)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">Az első 24 órában lehetős a fájl cseréje. A fájl neve nem fog megváltozni.</p>
              <Input id="game-file-upload-translator" type="file" onChange={handleFileChange} className="mt-1 bg-card text-card-foreground border-border" />
              {newGameFile && <p className="text-sm mt-2 text-green-400">Új fájl kiválasztva: {newGameFile.name}</p>}
            </div>
          </div>
        )}

        {/* Figyelmeztetés, ha a translator már nem csereléhet fájlt */}
        {!isAdmin && isCurrentUserUploader && !isWithin24Hours() && (
          <div className="border-t border-border pt-4 p-3 bg-yellow-900/20 border border-yellow-700 rounded-md">
            <p className="text-sm text-yellow-300">⏰ Az első 24 óra lejárt. Már nem tudod a fájlt cserélni, csak a leírást módosíthatod. Fordulj adminisztrátorhoz, ha szükséges a fájl cseréje.</p>
          </div>
        )}


        {/* Demo kapcsoló */}
        <div className="flex items-center space-x-2">
          <Switch checked={demo} onCheckedChange={setIsDemo} />
          <Label className="text-muted-foreground">Demo</Label>
        </div>

        {/* Banwarning kapcsoló */}
        <div className="flex items-center space-x-2">
          <Switch checked={banwarning} onCheckedChange={setBanwarning} />
          <Label className="text-muted-foreground">Multiplayer Game - Ban veszélyes</Label>
        </div>

        {/* Gombok */}
        <div className="flex gap-2 mt-4">
          <Button onClick={handleSaveClick} disabled={isSaving} className="flex-grow">
            {isSaving ? '⏳ Mentés közben...' : 'Mentés'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>Mégsem</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditDownloadForm;